/**
 * numeros.ts — Categoria "Números" (10 modelos premium)
 *
 * Resultados em destaque, montados com seção em camadas + `ui.stat`,
 * cartões e fotos do acervo compartilhado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote A)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para um número em destaque. */
function num(valor: string, rotulo: string, alinhamento = 'center') {
  return b('ui.stat', { value: valor, label: rotulo, align: alinhamento, motion: 'fade-up' });
}

const modelos: StudioPatternDefinition[] = [
  {
    key: 'numeros.tres-centralizados',
    category: 'numeros',
    label: 'Três números centralizados',
    description: 'Faixa limpa com três resultados lado a lado.',
    preview: 'stats',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', motion: 'fade' }, [
        num('+2.000', 'clientes atendidos'),
        num('98%', 'de satisfação'),
        num('12 anos', 'de mercado'),
      ]),
    ],
  },

  {
    key: 'numeros.faixa-escura',
    category: 'numeros',
    label: 'Faixa escura de resultados',
    description: 'Fundo escuro com brilho e quatro números em destaque.',
    preview: 'stats',
    outputs: WEB,
    order: 2,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade-up' },
        [
          num('R$ 42mi', 'movimentados por ano'),
          num('4,9', 'nota média dos clientes'),
          num('24h', 'para o primeiro retorno'),
          num('37', 'cidades atendidas'),
        ],
      ),
    ],
  },

  {
    key: 'numeros.com-titulo',
    category: 'numeros',
    label: 'Números com título',
    description: 'Título e texto de apoio acima de três resultados.',
    preview: 'stats',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', backgroundColor: '#f8fafc', motion: 'fade' }, [
        b('ui.title', { text: 'Números que falam por nós', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.paragraph', { text: 'Dados apurados nos últimos doze meses de operação.', align: 'center', maxWidth: '560px' }),
        num('+18mil', 'atendimentos concluídos'),
        num('92%', 'de recompra'),
        num('7 min', 'de tempo médio de resposta'),
      ]),
    ],
  },

  {
    key: 'numeros.ao-lado-foto',
    category: 'numeros',
    label: 'Números ao lado da foto',
    description: 'Foto da equipe à direita e resultados à esquerda.',
    preview: 'split-right',
    outputs: WEB,
    order: 4,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Crescimento constante, ano após ano', size: 'lg', level: 'h2' }),
        num('+64%', 'de crescimento no último ano', 'left'),
        num('120', 'profissionais no time', 'left'),
        b('ui.image', { src: FOTO.time, alt: 'Equipe reunida', ratio: 'landscape', radius: 'xl', shadow: 'lg' }),
      ]),
    ],
  },

  {
    key: 'numeros.cartoes',
    category: 'numeros',
    label: 'Resultados em cartões',
    description: 'Cada número dentro de um cartão com sombra.',
    preview: 'grid-3',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', layout: 'bento', pattern: 'grid', motion: 'fade' }, [
        b('ui.card', { icon: '↑', title: '+38% em vendas', text: 'Média dos clientes após seis meses.', variant: 'solid', radius: 'xl', shadow: 'md' }),
        b('ui.card', { icon: '⏱', title: '-52% de retrabalho', text: 'Menos tarefa repetida no dia a dia.', variant: 'solid', radius: 'xl', shadow: 'md' }),
        b('ui.card', { icon: '★', title: '4,9 de 5', text: 'Nota média de satisfação.', variant: 'solid', radius: 'xl', shadow: 'md' }),
      ]),
    ],
  },

  {
    key: 'numeros.sobre-foto',
    category: 'numeros',
    label: 'Números sobre a foto',
    description: 'Foto de fundo escurecida com três resultados por cima.',
    preview: 'stats',
    outputs: WEB,
    order: 6,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.logistica, overlay: 'darker', paddingY: 'xl', align: 'center', layout: 'bento', textColor: '#ffffff', maxWidth: '1080px', motion: 'fade' },
        [num('+1.400', 'entregas por dia'), num('99,2%', 'no prazo combinado'), num('16', 'centros de distribuição')],
      ),
    ],
  },

  {
    key: 'numeros.dois-grandes',
    category: 'numeros',
    label: 'Dois números grandes',
    description: 'Dois indicadores em tamanho grande, para páginas enxutas.',
    preview: 'grid-2',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '880px', layout: 'bento', backgroundGradient: 'soft', motion: 'fade-up' }, [
        num('R$ 3,2mi', 'economizados pelos clientes'),
        num('45 dias', 'para o retorno do investimento'),
      ]),
    ],
  },

  {
    key: 'numeros.com-selo',
    category: 'numeros',
    label: 'Números com selo de destaque',
    description: 'Selo, título curto e quatro resultados em grade.',
    preview: 'stats',
    outputs: WEB,
    order: 8,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', motion: 'fade' }, [
        b('ui.badge', { text: 'Balanço do ano', variant: 'outline', align: 'center' }),
        b('ui.title', { text: 'O ano em quatro números', size: 'md', level: 'h2', align: 'center' }),
        num('312', 'projetos entregues'),
        num('98%', 'de contratos renovados'),
        num('11', 'prêmios do setor'),
        num('0', 'incidentes de segurança'),
      ]),
    ],
  },

  {
    key: 'numeros.com-cta',
    category: 'numeros',
    label: 'Números com convite',
    description: 'Resultados seguidos de um botão de próximo passo.',
    preview: 'cta',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', layout: 'bento', backgroundColor: '#f8fafc', radius: 'xl', motion: 'fade-up' }, [
        num('+2.000', 'empresas atendidas'),
        num('4,9', 'de nota média'),
        b('ui.buttons', { items: 'Quero esses resultados|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'numeros.linha-do-tempo',
    category: 'numeros',
    label: 'Marcos da empresa',
    description: 'Números que contam a trajetória, com foto de apoio.',
    preview: 'split-left',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { layout: 'split-reverse', paddingY: 'lg', align: 'left', maxWidth: '1120px', pattern: 'dots', motion: 'fade' }, [
        b('ui.image', { src: FOTO.arquitetura, alt: 'Sede da empresa', ratio: 'portrait', radius: 'xl', shadow: 'md' }),
        b('ui.title', { text: 'Uma trajetória construída com clientes', size: 'lg', level: 'h2' }),
        num('2014', 'ano de fundação', 'left'),
        num('2019', 'primeira unidade própria', 'left'),
        num('2025', 'presença nacional', 'left'),
      ]),
    ],
  },
];

registerPatterns(modelos);
